import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Home, FileText, CreditCard, LineChart, UserCircle2,
  Lock, ArrowUpRight, ArrowDownRight, Minus,
  ChevronDown, CheckCircle2, Plus, Users, ChevronRight, Info
} from 'lucide-react';
import {
  getCategoryPill, getMerchantAvatar, formatReceiptDate,
  getGroupTypeConfig, getMemberAvatarColor, getMemberInitials
} from '../utils/formatters';
import {
  computeInsights, computeMemberSpend, insightsWindowStart,
  buildMonthRange, MINIMUM_RECEIPTS, ALL_KEY
} from '../utils/insightsCalculations';
import ScanReceiptModal from '../components/ScanReceiptModal';
import ReceiptDetailModal from '../components/ReceiptDetailModal';
import BuddyLogo from '../components/BuddyLogo';
import { markInsightsSeen } from '../utils/onboarding';

// ── Currency helpers ─────────────────────────────────────────────────────────
const CURRENCY_META = {
  GBP: { symbol: '£', bg: '#EEF2FF', color: '#2D5BFF', activeBg: '#2D5BFF', activeColor: '#FFFFFF' },
  INR: { symbol: '₹', bg: '#F0FDF4', color: '#16A34A', activeBg: '#16A34A', activeColor: '#FFFFFF' },
  USD: { symbol: '$', bg: '#FFF7ED', color: '#EA580C', activeBg: '#EA580C', activeColor: '#FFFFFF' },
  EUR: { symbol: '€', bg: '#FAF5FF', color: '#9333EA', activeBg: '#9333EA', activeColor: '#FFFFFF' },
  AED: { symbol: 'د.إ', bg: '#ECFDF5', color: '#059669', activeBg: '#059669', activeColor: '#FFFFFF' },
  AUD: { symbol: 'A$', bg: '#FFF1F2', color: '#E11D48', activeBg: '#E11D48', activeColor: '#FFFFFF' },
};
const DEFAULT_META = { symbol: '', bg: '#F3F4F6', color: '#374151', activeBg: '#374151', activeColor: '#FFFFFF' };
const getCurrencyMeta = (cur) => CURRENCY_META[(cur || '').toUpperCase()] || DEFAULT_META;
const formatCurrency = (amount, currency) => {
  const meta = getCurrencyMeta(currency);
  return `${meta.symbol}${(amount || 0).toFixed(2)}`;
};

// ── Currency chips ───────────────────────────────────────────────────────────
const CurrencyChips = ({ currencies, selected, onSelect }) => {
  if (!currencies || currencies.length <= 1) return null;
  return (
    <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-gray-100">
      <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
        Currency
      </span>
      <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {currencies.map((cur) => {
          const meta = getCurrencyMeta(cur);
          const isSelected = cur === selected;
          return (
            <button
              key={cur}
              onClick={() => onSelect(cur)}
              className="flex-shrink-0 flex items-center gap-1 rounded-full transition-all"
              style={{
                backgroundColor: isSelected ? meta.activeBg : meta.bg,
                color: isSelected ? meta.activeColor : meta.color,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif',
                border: `1.5px solid ${isSelected ? meta.activeBg : meta.color}22`,
                boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              <span style={{ fontSize: 12 }}>{meta.symbol}</span>
              {cur}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_HEADER = 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)';
const PRIMARY = '#2D5BFF';

const NAV_ITEMS = [
  { icon: Home, label: 'Home', key: 'home' },
  { icon: FileText, label: 'Receipts', key: 'receipts' },
  { icon: CreditCard, label: 'Cards', key: 'cards' },
  { icon: LineChart, label: 'Insights', key: 'insights' },
  { icon: UserCircle2, label: 'Profile', key: 'profile' },
];

const UserAvatar = ({ name, avatarUrl, size = 40, fontSize = 14, bg = PRIMARY, textColor = '#FFFFFF' }) => {
  const initial = (name || 'U').charAt(0).toUpperCase();
  return (
    <div
      className="flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        backgroundColor: avatarUrl ? 'transparent' : bg,
        color: textColor,
        fontWeight: 700,
        fontSize,
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        initial
      )}
    </div>
  );
};

const MerchantAvatar = ({ name }) => {
  const { initials, bgColor, textColor } = getMerchantAvatar(name);
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 600,
        fontSize: 14,
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {initials}
    </div>
  );
};

const CategoryPillIcon = ({ category }) => {
  const pill = getCategoryPill(category);
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: pill.bg,
      }}
    >
      <div style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: pill.color }} />
    </div>
  );
};

// ── Group switcher (identical pattern to Dashboard / Receipts) ───────────────
const GroupSwitcherPill = ({ selectedGroup, avatarUrl, userName, onClick }) => {
  if (selectedGroup) {
    const cfg = getGroupTypeConfig(selectedGroup.type);
    const Icon = cfg.icon;
    return (
      <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '6px 14px 6px 8px', flexShrink: 0 }}>
        <div className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 15 }}>
          {selectedGroup.emoji
            ? <span style={{ lineHeight: 1 }}>{selectedGroup.emoji}</span>
            : <Icon className="w-3.5 h-3.5" style={{ color: '#FFFFFF' }} />}
        </div>
        <span className="truncate" style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', maxWidth: 100 }}>{selectedGroup.name}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
      </button>
    );
  }
  const initial = (userName || 'U').charAt(0).toUpperCase();
  return (
    <button onClick={onClick} className="flex items-center gap-2" style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '6px 14px 6px 8px', flexShrink: 0 }}>
      <div className="flex items-center justify-center overflow-hidden" style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: avatarUrl ? 'transparent' : 'rgba(255,255,255,0.2)', color: '#FFFFFF', fontWeight: 700, fontSize: 11, fontFamily: 'Manrope, sans-serif' }}>
        {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
      </div>
      <span style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>Personal</span>
      <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
    </button>
  );
};

const GroupSwitcherSheet = ({ isOpen, onClose, groups, selectedGroupId, onSelect, onCreateGroup, onManageGroups, userName, avatarUrl }) => {
  if (!isOpen) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} />
      <div className="fixed left-0 right-0 bottom-0 z-50" style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '12px 20px 32px', maxHeight: '70vh', overflowY: 'auto', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
        <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
        <p style={{ color: '#6B7280', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', letterSpacing: 1, marginBottom: 16 }}>SWITCH TO</p>
        <button onClick={() => onSelect(null)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
          <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? 'transparent' : PRIMARY, color: '#FFFFFF', fontWeight: 700, fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>
            {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (userName || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Personal</p>
            <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>Just your spending</p>
          </div>
          {!selectedGroupId && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
        </button>
        {groups.map((g) => {
          const cfg = getGroupTypeConfig(g.type);
          const Icon = cfg.icon;
          return (
            <button key={g.id} onClick={() => onSelect(g)} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 20 }}>
                {g.emoji
                  ? <span style={{ lineHeight: 1 }}>{g.emoji}</span>
                  : <Icon className="w-5 h-5" style={{ color: '#FFFFFF' }} />}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p style={{ color: '#111827', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>{g.name}</p>
                <p style={{ color: '#9CA3AF', fontSize: 13, fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>{cfg.label} · {g.member_count} member{g.member_count !== 1 ? 's' : ''}</p>
              </div>
              {selectedGroupId === g.id && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: PRIMARY }} />}
            </button>
          );
        })}
        <div style={{ marginTop: 8 }}>
          <button onClick={onCreateGroup} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6' }}><Plus className="w-5 h-5" style={{ color: PRIMARY }} /></div>
            <p style={{ color: PRIMARY, fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Create a circle</p>
          </button>
          <button onClick={onManageGroups} className="w-full flex items-center gap-3 py-3">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: '#F3F4F6' }}><Users className="w-5 h-5" style={{ color: '#6B7280' }} /></div>
            <p style={{ color: '#6B7280', fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>Manage circles</p>
          </button>
        </div>
      </div>
    </>
  );
};

// ── Group: Spend by member card (with tap-to-expand breakdown) ───────────────
const MemberSpendCard = ({ members, labelSpend, activeCurrency, formatCurrency }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedLabel, setExpandedLabel] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [showZeroSpend, setShowZeroSpend] = useState(false);

  const hasLabels = labelSpend && labelSpend.length > 0;
  const activeMembers = members.filter(m => m.total > 0);
  const zeroMembers   = members.filter(m => m.total === 0);
  const allTotals = [...members, ...(labelSpend || [])];
  const maxTotal  = allTotals.reduce((mx, m) => Math.max(mx, m.total), 0) || 1;

  return (
    <div className="bg-white rounded-3xl p-5 shadow-sm mb-4">
      {/* Header + tabs */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 font-manrope text-base">Spend breakdown</h3>
        {hasLabels && (
          <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
            {['members','labels'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="px-3 py-1 text-xs font-bold font-dmsans capitalize transition-colors"
                style={{ backgroundColor: activeTab === tab ? '#2D5BFF' : 'transparent', color: activeTab === tab ? '#fff' : '#6B7280', border: 'none', cursor: 'pointer' }}>
                {tab === 'members' ? `Members (${activeMembers.length})` : `Sources (${labelSpend.length})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Members tab ────────────────────────────────────── */}
      {activeTab === 'members' && (
        <>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {activeMembers.map((m) => {
              const colour = getMemberAvatarColor(m.name);
              const pct = Math.round((m.total / maxTotal) * 100);
              const isExpanded = expandedId === m.user_id;
              const canExpand = m.count > 0;
              return (
                <div key={m.user_id}>
                  <button onClick={() => canExpand && setExpandedId(isExpanded ? null : m.user_id)}
                    className="w-full flex items-center gap-3 py-2 text-left"
                    style={{ cursor: canExpand ? 'pointer' : 'default' }}>
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: colour, color: '#FFFFFF', fontSize: 12, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                      {getMemberInitials(m.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-end mb-1">
                        <span className="font-medium text-gray-900 text-sm font-dmsans truncate">{m.name}</span>
                        <span className="font-bold text-gray-900 font-manrope flex-shrink-0 ml-2">{formatCurrency(m.total, activeCurrency)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colour }} />
                        </div>
                        <span className="text-xs text-gray-400 font-dmsans flex-shrink-0">{m.count} {m.count === 1 ? 'receipt' : 'receipts'}</span>
                      </div>
                    </div>
                    {canExpand && <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: '#9CA3AF', transform: isExpanded ? 'rotate(90deg)' : 'none' }} />}
                  </button>
                  {isExpanded && (
                    <div className="ml-12 mb-2 mt-1 pl-3" style={{ borderLeft: '2px solid #EEF2FF' }}>
                      {m.months && m.months.length > 1 ? m.months.map((mon) => (
                        <div key={mon.key} className="mb-2 last:mb-0">
                          <div className="flex items-center justify-between pt-1.5 pb-1">
                            <span className="text-gray-500 text-xs font-bold font-dmsans uppercase tracking-wide">{mon.label}</span>
                            <span className="text-gray-500 text-xs font-bold font-manrope">{formatCurrency(mon.total, activeCurrency)}</span>
                          </div>
                          {mon.receipts.map((r, idx) => (
                            <div key={`${r.id}-${idx}`} className="flex items-start justify-between py-1.5">
                              <div className="min-w-0 flex-1 flex flex-col">
                                <span className="text-gray-700 text-sm font-dmsans truncate">{r.merchant}</span>
                                <span className="text-gray-400 text-xs font-dmsans mt-0.5">{formatReceiptDate(r.date)}</span>
                              </div>
                              <span className="text-gray-900 text-sm font-semibold font-manrope flex-shrink-0 ml-2 mt-0.5">{formatCurrency(r.amount, activeCurrency)}</span>
                            </div>
                          ))}
                        </div>
                      )) : (m.receipts || []).map((r, idx) => (
                        <div key={`${r.id}-${idx}`} className="flex items-start justify-between py-1.5">
                          <div className="min-w-0 flex-1 flex flex-col">
                            <span className="text-gray-700 text-sm font-dmsans truncate">{r.merchant}</span>
                            <span className="text-gray-400 text-xs font-dmsans mt-0.5">{formatReceiptDate(r.date)}</span>
                          </div>
                          <span className="text-gray-900 text-sm font-semibold font-manrope flex-shrink-0 ml-2 mt-0.5">{formatCurrency(r.amount, activeCurrency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Zero-spend members collapse */}
          {zeroMembers.length > 0 && (
            <div style={{ borderTop: '1px solid #F3F4F6', marginTop: 8, paddingTop: 8 }}>
              <button onClick={() => setShowZeroSpend(v => !v)}
                className="w-full flex items-center justify-between py-1"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <span style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>
                  {showZeroSpend ? '▲' : '▾'} {zeroMembers.length} member{zeroMembers.length !== 1 ? 's' : ''} with no spend
                </span>
              </button>
              {showZeroSpend && (
                <div style={{ paddingTop: 4 }}>
                  {zeroMembers.map(m => (
                    <div key={m.user_id} className="flex items-center gap-3 py-1.5">
                      <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: getMemberAvatarColor(m.name), color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: 'Manrope, sans-serif', opacity: 0.5 }}>
                        {getMemberInitials(m.name)}
                      </div>
                      <span style={{ fontSize: 13, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif' }}>{m.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#D1D5DB', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>£0.00</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Sources tab ────────────────────────────────────── */}
      {activeTab === 'labels' && hasLabels && (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {labelSpend.map((lbl) => {
            const pct = Math.round((lbl.total / maxTotal) * 100);
            const isOpen = expandedLabel === lbl.name;
            const receipts = lbl.receipts || [];
            return (
              <div key={lbl.name}>
                <button onClick={() => setExpandedLabel(isOpen ? null : lbl.name)}
                  className="w-full flex items-center gap-3 py-2"
                  style={{ background: 'none', border: 'none', cursor: receipts.length > 0 ? 'pointer' : 'default', textAlign: 'left' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#0D9488', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: '#fff' }}>£</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-end mb-1">
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#065F46', fontFamily: 'DM Sans, sans-serif' }}>{lbl.name}</span>
                      <div className="flex items-center gap-1.5">
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#0D9488', fontFamily: 'Manrope, sans-serif' }}>{formatCurrency(lbl.total, activeCurrency)}</span>
                        {receipts.length > 0 && <span style={{ fontSize: 11, color: '#0D9488', transform: isOpen ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.2s' }}>▾</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#CCFBF1' }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#0D9488' }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#0D9488', fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
                        {receipts.length} receipt{receipts.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </button>
                {isOpen && receipts.length > 0 && (
                  <div style={{ marginLeft: 44, marginBottom: 6, borderLeft: '2px solid #CCFBF1', paddingLeft: 12, maxHeight: 200, overflowY: 'auto' }}>
                    {receipts.map((r, idx) => (
                      <div key={r.id || idx} className="flex items-start justify-between py-1.5" style={{ borderBottom: '1px solid #F0FDF4' }}>
                        <div className="min-w-0 flex-1 flex flex-col">
                          <span className="truncate" style={{ fontSize: 13, color: '#065F46', fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>{r.merchant || 'Unknown'}</span>
                          <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'DM Sans, sans-serif', marginTop: 1 }}>
                            {r.transaction_date ? new Date(r.transaction_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                            {r.shared_by_name ? ` · by ${r.shared_by_name.split(' ')[0]}` : ''}
                          </span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0D9488', fontFamily: 'Manrope, sans-serif', flexShrink: 0, marginLeft: 8 }}>
                          {formatCurrency(r.amount || 0, activeCurrency)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


export default function InsightsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Parse "YYYY-MM-DD" as local midnight (not UTC)
  const parseLocalDate = (raw) => {
    if (!raw) return new Date(NaN);
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(raw);
  };
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [showScanModal, setShowScanModal] = useState(false);
  const [showAllMerchants, setShowAllMerchants] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [showReceiptDetail, setShowReceiptDetail] = useState(false);

  // ── Group view state ──
  const [showGroupSwitcher, setShowGroupSwitcher] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupReceipts, setGroupReceipts] = useState([]);
  const [groupDataRefreshKey, setGroupDataRefreshKey] = useState(0); // increment to force refetch
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [reimbursementSummary, setReimbursementSummary] = useState(null);
  const [showOutstandingModal, setShowOutstandingModal] = useState(false);
  const [outstandingReceipts, setOutstandingReceipts] = useState([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [markingReceived, setMarkingReceived] = useState(null); // share_id being marked
  const [expandedMarkId, setExpandedMarkId] = useState(null);
  const [markNotes, setMarkNotes] = useState({});
  const isGroupView = !!selectedGroup;

  // Mark the onboarding "Peek at your Insights" step complete on visit.
  useEffect(() => {
    markInsightsSeen();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [rawReceipts, setRawReceipts] = useState([]);

  // A receipt's effective date: transaction_date primary, created_at fallback
  // (mirrors the server-side window filter so nothing is dropped client-side).
  const effectiveDate = (r) => parseLocalDate(r.transaction_date || r.created_at);

  const fetchReceipts = useCallback(async () => {
    try {
      setLoading(true);
      const since = insightsWindowStart(3);
      const res = await axios.get(`${API}/receipts`, {
        params: { since },
        withCredentials: true,
      });
      setRawReceipts(res.data.receipts || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Fetch groups for the switcher + restore persisted selection
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`, { withCredentials: true });
        const fetched = res.data.groups || [];
        setGroups(fetched);
        try {
          const saved = sessionStorage.getItem('mb_selected_group');
          if (saved) {
            const parsed = JSON.parse(saved);
            const match = fetched.find(g => g.id === parsed.id);
            if (match) setSelectedGroup(match);
          }
        } catch { /* ignore */ }
      } catch { /* silent */ }
    };
    fetchGroups();
  }, []);

  // Fetch group receipts + members when a group is selected
  useEffect(() => {
    if (!selectedGroup) { setGroupReceipts([]); setGroupMembers([]); return; }
    const fetchGroupData = async () => {
      setGroupLoading(true);
      try {
        const [itemsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/groups/${selectedGroup.id}/items?type=receipt`, { withCredentials: true }),
          axios.get(`${API}/groups/${selectedGroup.id}/summary`, { withCredentials: true }),
        ]);
        // Flatten to a receipt shape computeInsights understands.
        // Attribution: if a member paid (paid_by_type === 'member'), attribute spend
        // to them — not to who shared the receipt. Snehal shares Sam's receipt →
        // spend shows under Sam, not Snehal. Labels fall back to shared_by.
        const flattened = (itemsRes.data.items || []).map(i => {
          const labelId   = i.paid_by_label_id || '';
          const received  = i.reimbursement_status === 'received';
          const paidByMember = i.paid_by_type === 'member' && i.paid_by_name;

          // Case 2: member paid + received by a person → reroute to that person
          const reimbByMemberReceived = received && labelId.startsWith('user_') && i.paid_by_label_name;

          return {
            ...i.item,
            // For member rows: route to the correct name
            // Case 1 (label paid) and Case 2 (received) are excluded by the filter below,
            // so shared_by here only matters for Case 3 (pending member payer)
            shared_by: reimbByMemberReceived
              ? labelId                                    // Snehal's user_id
              : paidByMember ? (i.paid_by_user_id || i.shared_by) : i.shared_by,
            shared_by_name: reimbByMemberReceived
              ? i.paid_by_label_name                       // "Snehal Kakade"
              : paidByMember ? i.paid_by_name : i.shared_by_name,
            _reimbursement_status: i.reimbursement_status,
            _paid_by_type:         i.paid_by_type,
            _paid_by_label_name:   i.paid_by_label_name,
            _paid_by_label_id:     labelId,
          };
        });
        setGroupReceipts(flattened);
        setGroupMembers(summaryRes.data.members || []);
        // Reimbursement summary
        const summary = summaryRes.data.summary || {};
        setReimbursementSummary({
          pending_total: summary.pending_reimbursements || 0,
          pending_count: summary.pending_count || 0,
          enabled: summary.reimbursement_enabled || false,
          label_spend: summary.label_spend || [],
        });
      } catch { /* silent */ }
      finally { setGroupLoading(false); }
    };
    fetchGroupData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, groupDataRefreshKey]);

  // When switching context (Personal ↔ group), clear the user's explicit month pick
  // so the derived default (ALL for finite groups, current month otherwise) re-applies.
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setShowGroupSwitcher(false);
    setSelectedMonthKey(null);
    setSelectedCurrency(null);
    try {
      if (group) sessionStorage.setItem('mb_selected_group', JSON.stringify({ id: group.id }));
      else sessionStorage.removeItem('mb_selected_group');
    } catch { /* ignore */ }
  };

  // NOTE: period selection is DERIVED inside the compute effect below.
  // `selectedMonthKey` holds only the user's explicit chip tap; null = use the default.

  useEffect(() => {
    if (isGroupView && groupLoading) return;

    // Source receipts: group view uses shared receipts, personal uses own.
    const sourceReceipts = isGroupView ? groupReceipts : rawReceipts;

    // Personal: only completed receipts (in-progress ones aren't ready).
    // Group: include all shared receipts regardless of scan_status — they have
    // valid amounts and were deliberately shared to the circle.
    const completedReceipts = isGroupView
      ? sourceReceipts
      : sourceReceipts.filter((r) => !r.scan_status || r.scan_status === 'completed');

    // ── Derived period selection — single source of truth, no effect race ──────
    // `selectedMonthKey` = only an explicit chip tap (null = never tapped).
    // `effectiveMonthKey` = what to actually show, derived every render:
    //   • finite groups (trip/event/custom) default to ALL (full history)
    //   • everything else defaults to the current month
    const finiteGroupType = isGroupView && ['trip', 'event', 'custom'].includes(selectedGroup.type);
    const currentMonthKey = buildMonthRange(1)[0].key;
    const effectiveMonthKey = selectedMonthKey || (finiteGroupType ? ALL_KEY : (isGroupView ? ALL_KEY : currentMonthKey));

    const isAllSel = effectiveMonthKey === ALL_KEY;
    // allHistory: finite group + ALL = bypass the 3-month bucket cap entirely
    const allHistory = isAllSel && (finiteGroupType || isGroupView); // group view = always all time

    // Derive currencies from the selected period
    let scopeReceipts;
    if (allHistory) {
      scopeReceipts = completedReceipts.slice();
    } else if (!isAllSel) {
      const parts = effectiveMonthKey.split('-');
      const yr = parseInt(parts[0]);
      const mo = parseInt(parts[1]);
      scopeReceipts = completedReceipts.filter((r) => {
        const d = effectiveDate(r);
        return !isNaN(d) && d.getFullYear() === yr && d.getMonth() + 1 === mo;
      });
    } else {
      const windowStart = parseLocalDate(insightsWindowStart(3));
      scopeReceipts = completedReceipts.filter((r) => {
        const d = effectiveDate(r);
        return !isNaN(d) && d >= windowStart;
      });
    }

    // Derive currencies and sort by receipt count (most-used = default)
    const currencyCounts = {};
    scopeReceipts.forEach((r) => {
      const cur = (r.currency || '').toUpperCase();
      if (cur) currencyCounts[cur] = (currencyCounts[cur] || 0) + 1;
    });
    const currencies = Object.keys(currencyCounts).sort(
      (a, b) => currencyCounts[b] - currencyCounts[a]
    );

    const activeCurrency =
      (selectedCurrency && currencies.includes(selectedCurrency))
        ? selectedCurrency
        : (currencies[0] || 'GBP');

    if (!selectedCurrency && currencies.length > 0) {
      setSelectedCurrency(activeCurrency);
    }

    // Filter completed receipts by the active currency
    const currencyFiltered = completedReceipts.filter(
      (r) => (r.currency || '').toUpperCase() === activeCurrency
    );

    // Resolve the selected descriptor for computeInsights
    let selectedM = null;
    if (isAllSel) {
      selectedM = { key: ALL_KEY };
    } else {
      selectedM = computeInsights(currencyFiltered).monthRange.find(
        m => m.key === effectiveMonthKey
      ) || null;
    }
    // Pass allHistory so computeInsights bypasses the 3-month bucket for finite groups
    const computed = computeInsights(currencyFiltered, selectedM, { allHistory });

    // Global threshold check — all completed receipts regardless of currency
    const globalTotalReceiptCount = completedReceipts.length;
    const globalHasEnoughData = globalTotalReceiptCount >= MINIMUM_RECEIPTS;

    // Member spend (group view only) — scoped to the effective period
    const memberSpend = isGroupView
      ? computeMemberSpend(
          currencyFiltered.filter((r) => {
            // Mirror the 3-case server logic:
            // Case 1: label paid directly → in labelSpend, not member rows
            if (r._paid_by_type === 'label' && r._paid_by_label_name) return false;
            // Case 2: member paid + reimbursement received → moved to label/member row
            if (r._reimbursement_status === 'received' && r._paid_by_label_name) return false;
            if (allHistory) return true; // finite ALL = every receipt, any age
            if (isAllSel) {
              const d = effectiveDate(r);
              const windowStart = parseLocalDate(insightsWindowStart(3));
              return !isNaN(d) && d >= windowStart;
            }
            const parts = effectiveMonthKey.split('-');
            const yr = parseInt(parts[0]);
            const mo = parseInt(parts[1]);
            const d = effectiveDate(r);
            return !isNaN(d) && d.getFullYear() === yr && d.getMonth() + 1 === mo;
          }),
          groupMembers
        )
      : [];

    let finalData = {
      ...computed,
      allCurrencies: currencies,
      activeCurrency,
      globalHasEnoughData,
      globalTotalReceiptCount,
      memberSpend,
      isAll: isAllSel,
      effectiveMonthKey,
    };

    // Override path (single-month, below global threshold) — personal only
    if (!isGroupView && !computed.hasEnoughData && !isAllSel) {
      const parts = effectiveMonthKey.split('-');
      const yr = parseInt(parts[0]);
      const mo = parseInt(parts[1]);

      const monthReceipts = currencyFiltered.filter((r) => {
        const d = effectiveDate(r);
        return !isNaN(d) && d.getFullYear() === yr && d.getMonth() + 1 === mo;
      });

      const overrideTotal = monthReceipts.reduce((s, r) => s + (r.amount || 0), 0);
      const overrideCount = monthReceipts.length;

      const catMap = {};
      monthReceipts.forEach((r) => {
        const cat = r.category || 'Other';
        catMap[cat] = (catMap[cat] || 0) + (r.amount || 0);
      });
      const catTotal = Object.values(catMap).reduce((s, v) => s + v, 0);
      const overrideCategories = Object.entries(catMap)
        .map(([name, total]) => ({
          name,
          total,
          pct: catTotal > 0 ? Math.round((total / catTotal) * 100) : 0,
        }))
        .sort((a, b) => b.total - a.total);

      const merMap = {};
      monthReceipts.forEach((r) => {
        const name = r.merchant || 'Unknown';
        if (!merMap[name]) merMap[name] = { total: 0, count: 0 };
        merMap[name].total += r.amount || 0;
        merMap[name].count += 1;
      });
      const overrideMerchants = Object.entries(merMap)
        .map(([name, d]) => ({ name, total: d.total, count: d.count }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

      finalData = {
        ...finalData,
        selTotal: overrideTotal,
        selCount: overrideCount,
        categories: overrideCategories,
        topMerchants: overrideMerchants,
      };
    }

    setInsightsData(finalData);
  }, [rawReceipts, groupReceipts, groupMembers, isGroupView, groupLoading, selectedMonthKey, selectedCurrency, selectedGroup]);

  const fetchOutstandingReceipts = async () => {
    if (!selectedGroup) return;
    setOutstandingLoading(true);
    try {
      const res = await axios.get(`${API}/groups/${selectedGroup.id}/outstanding-reimbursements`, { withCredentials: true });
      setOutstandingReceipts(res.data.receipts || []);
    } catch (err) {
      console.error('Outstanding reimbursements fetch failed:', err?.response?.data || err);
    } finally { setOutstandingLoading(false); }
  };

  const handleOpenOutstanding = () => {
    setShowOutstandingModal(true);
    fetchOutstandingReceipts();
  };

  const handleMarkReceivedInsights = async (shareId, groupId) => {
    setMarkingReceived(shareId);
    try {
      await axios.post(`${API}/groups/${groupId}/share/${shareId}/mark-received`, { note: markNotes[shareId] || null }, { withCredentials: true });
      // Remove from outstanding modal immediately
      setOutstandingReceipts(prev => prev.filter(r => r.share_id !== shareId));
      setExpandedMarkId(null);
      // Refetch full group data so member spend + label spend recalculate correctly
      setGroupDataRefreshKey(k => k + 1);
    } catch { /* silent */ }
    finally { setMarkingReceived(null); }
  };

  const handleReceiptClick = (id) => {
    setSelectedReceiptId(id);
    setShowReceiptDetail(true);
  };

  const handleReceiptDelete = (deletedId) => {
    setRawReceipts((prev) => prev.filter((r) => r.id !== deletedId));
  };

  const onNavClick = (key) => {
    if (key === 'home') navigate('/dashboard');
    else if (key === 'receipts') navigate('/receipts');
    else if (key === 'cards') navigate('/cards');
    else if (key === 'insights') navigate('/insights');
    else if (key === "profile") { navigate("/profile");}
  };

  if (loading || !insightsData) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const {
    hasEnoughData,
    totalReceiptCount,
    monthRange,
    selectedMonth,
    selTotal,
    selCount,
    vsLastMonthPct,
    vsLastMonthDir,
    categories,
    topMerchants,
    allCurrencies,
    activeCurrency,
    globalHasEnoughData,
    globalTotalReceiptCount,
    memberSpend,
    isAll,
  } = insightsData;

  // Three render states:
  // 1. Not enough receipts globally → lock screen  (PERSONAL ONLY)
  // 2. Enough but no data for selected period → "no data" card
  // 3. Has data → full insights
  // Group view is never locked behind the 6-receipt threshold.
  const isUnlocked = isGroupView ? true : globalHasEnoughData;
  const hasMonthData = selCount > 0;

  // Finite group types (trip/event/custom) show full history as a single "All"
  // view — no month chips. Ongoing types (household/community) + personal browse by month.
  const finiteGroup = isGroupView && ['trip', 'event', 'custom'].includes(selectedGroup.type);

  // Derived effective selection for chip highlighting (mirrors the compute effect logic)
  const renderCurrentMonthKey = buildMonthRange(1)[0].key;
  const renderEffectiveMonthKey = selectedMonthKey || (finiteGroup ? ALL_KEY : (isGroupView ? ALL_KEY : renderCurrentMonthKey));

  // Type-aware caption describing the data window beneath the header.
  const windowCaption = (() => {
    if (!isGroupView) return 'Last 3 months · tap a month to explore';
    switch (selectedGroup.type) {
      case 'trip':   return 'All trip expenses';
      case 'event':  return 'All event expenses';
      case 'custom': return 'All expenses';
      default:       return 'All time'; // household, community → all time
    }
  })();

  const renderEmptyState = () => {
    const receiptsNeeded = MINIMUM_RECEIPTS - globalTotalReceiptCount;
    const progressPct = Math.min((globalTotalReceiptCount / MINIMUM_RECEIPTS) * 100, 100);

    return (
      <div className={`px-4 py-6 ${!isMobile ? 'max-w-2xl mx-auto' : ''}`} style={{ paddingBottom: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 34px))' : 40 }}>
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Lock className="w-8 h-8 text-blue-300" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 font-manrope mb-2">Unlock your insights</h2>
          <p className="text-gray-500 text-sm font-dmsans mb-8">
            Scan a few more receipts and we'll<br/>show your complete spending story
          </p>

          <div className="text-left mb-8">
            <div className="flex justify-between text-sm font-dmsans mb-2">
              <span className="text-gray-600">Receipts scanned</span>
              <span className="font-bold text-gray-900">{globalTotalReceiptCount} / {MINIMUM_RECEIPTS}</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${progressPct}%` }}></div>
            </div>
          </div>

          <button
            onClick={() => setShowScanModal(true)}
            className="text-blue-600 font-bold text-sm font-dmsans py-2"
          >
            Scan {receiptsNeeded} more {receiptsNeeded === 1 ? 'receipt' : 'receipts'} to unlock
          </button>
        </div>

        {/* Ghost UI Elements */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-4 opacity-40">
          <h3 className="font-bold text-gray-400 font-manrope mb-4">Spending by category</h3>
          {[1,2,3].map(i => (
            <div key={i} className="flex items-center justify-between mb-4 last:mb-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                <div>
                  <div className="w-24 h-3 bg-gray-200 rounded-full mb-2"></div>
                  <div className="w-40 h-2 bg-gray-100 rounded-full"></div>
                </div>
              </div>
              <div className="w-12 h-3 bg-gray-200 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderNoMonthData = () => {
    const periodLabel = isAll ? 'the last 3 months' : (selectedMonth?.labelFull || 'this month');
    return (
      <div className={`px-4 py-6 ${!isMobile ? 'max-w-2xl mx-auto' : ''}`} style={{ paddingBottom: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 34px))' : 40 }}>
        <div className="bg-white rounded-3xl p-6 shadow-sm text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
            <FileText className="w-7 h-7 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 font-manrope mb-2">
            {isGroupView ? 'Nothing shared yet' : 'No spending data'}
          </h3>
          <p className="text-gray-500 text-sm font-dmsans">
            {isGroupView
              ? `No receipts shared in this group for ${periodLabel}.`
              : `No receipts scanned for ${periodLabel}`}
          </p>
        </div>
      </div>
    );
  };

  const renderInsights = () => {
    return (
      <div className={`px-4 py-6 ${!isMobile ? 'max-w-2xl mx-auto' : ''}`} style={{ paddingBottom: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 34px))' : 40 }}>
        {/* Outstanding reimbursements — group view only */}
        {isGroupView && reimbursementSummary?.enabled && reimbursementSummary.pending_total > 0 && (
          <button
            onClick={handleOpenOutstanding}
            className="w-full flex items-center gap-3 rounded-3xl mb-4"
            style={{ backgroundColor: '#FFFBEB', border: '1px solid #FCD34D', padding: '12px 16px', cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 18 }}>⏳</span>
            </div>
            <div className="flex-1">
              <p style={{ fontSize: 11, fontWeight: 700, color: '#92400E', fontFamily: 'DM Sans, sans-serif' }}>Outstanding reimbursements</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#D97706', fontFamily: 'Manrope, sans-serif', lineHeight: 1.2 }}>{formatCurrency(reimbursementSummary.pending_total, activeCurrency)}</p>
              <p style={{ fontSize: 11, color: '#B45309', fontFamily: 'DM Sans, sans-serif' }}>{reimbursementSummary.pending_count} receipt{reimbursementSummary.pending_count !== 1 ? 's' : ''} pending · tap to view</p>
            </div>
            <span style={{ fontSize: 18, color: '#D97706' }}>›</span>
          </button>
        )}

        {/* Spend by member — group view only, headline card */}
        {isGroupView && memberSpend && memberSpend.length > 0 && (
          <MemberSpendCard
            members={memberSpend}
            labelSpend={(reimbursementSummary?.label_spend || []).map(lbl => ({
              ...lbl,
              // Case 1: label paid directly (not_applicable) OR Case 2: member reimbursed (received)
              receipts: groupReceipts.filter(r =>
                r._paid_by_label_name === lbl.name &&
                (r._reimbursement_status === 'received' || r._paid_by_type === 'label')
              ),
            }))}
            activeCurrency={activeCurrency}
            formatCurrency={formatCurrency}
          />
        )}

        {/* Categories — horizontal scrollable chips */}
        <div className="bg-white rounded-3xl p-5 shadow-sm mb-4">
          <h3 className="font-bold text-gray-900 font-manrope text-base mb-4">Spending by category</h3>
          {categories.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
              {categories.map((cat) => {
                const pill = getCategoryPill(cat.name);
                return (
                  <div key={cat.name} className="flex-shrink-0 flex flex-col items-center rounded-2xl px-4 py-3"
                    style={{ backgroundColor: pill.color + '15', border: `1.5px solid ${pill.color}30`, minWidth: 100 }}>
                    <CategoryPillIcon category={cat.name} />
                    <span className="text-xs font-semibold font-dmsans mt-2 text-center" style={{ color: '#374151' }}>{cat.name}</span>
                    <span className="font-bold font-manrope mt-1" style={{ color: pill.color, fontSize: 15 }}>{formatCurrency(cat.total, activeCurrency)}</span>
                    <span className="text-xs text-gray-400 font-dmsans">{cat.pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm font-dmsans text-center py-4">No spending data yet.</p>
          )}
        </div>

        {/* Top Merchants — top 3 with expand */}
        {topMerchants && topMerchants.length > 0 && (() => {
          const visible = showAllMerchants ? topMerchants : topMerchants.slice(0, 3);
          return (
            <div className="bg-white rounded-3xl p-5 shadow-sm mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 font-manrope text-base">Top merchants</h3>
                {topMerchants.length > 3 && (
                  <button onClick={() => setShowAllMerchants(v => !v)}
                    style={{ fontSize: 12, fontWeight: 600, color: '#2D5BFF', fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {showAllMerchants ? 'Show less' : `See all ${topMerchants.length}`}
                  </button>
                )}
              </div>
              <div className="space-y-0">
                {visible.map((merchant, idx) => (
                  <div key={idx} className="w-full flex items-center justify-between py-3" style={{ borderBottom: idx < visible.length - 1 ? '1px solid #F9FAFB' : 'none' }}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <MerchantAvatar name={merchant.name} />
                      <div className="text-left min-w-0 flex-1">
                        <p className="font-semibold truncate text-gray-900 text-[15px] font-manrope">{merchant.name}</p>
                        <span className="text-gray-400 text-xs font-dmsans">{merchant.count} {merchant.count === 1 ? 'visit' : 'visits'}</span>
                      </div>
                    </div>
                    <p className="font-semibold text-gray-900 text-[15px] font-manrope flex-shrink-0 ml-3">
                      {formatCurrency(merchant.total, activeCurrency)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const userName = user?.first_name || 'User';
  const lastName = user?.last_name || '';
  const userEmail = user?.email || '';
  const avatarUrl = user?.avatar_url;

  const content = (
    <>
      {/* Header Area */}
      <header
        style={{
          background: GRADIENT_HEADER,
          paddingTop: isMobile ? 48 : 36,
          paddingBottom: (isUnlocked && hasMonthData) ? 32 : 24,
          borderBottomLeftRadius: isMobile ? 28 : 0,
          borderBottomRightRadius: isMobile ? 28 : 0,
          color: '#fff'
        }}
        className={`px-5 relative z-10 ${!isMobile ? 'mx-0 px-10' : ''}`}
      >
        <div className={`mx-auto ${!isMobile ? 'max-w-2xl px-0' : ''}`}>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold font-manrope mb-1">
                {isGroupView ? (selectedGroup?.name || 'Circle') : 'Insights'}
              </h1>
              {isGroupView ? (
                <>
                  <div className="flex items-center gap-2 mb-1" style={{ flexWrap: 'nowrap' }}>
                    <div className="flex items-center flex-shrink-0">
                      {groupMembers.slice(0, 3).map((m, i) => {
                        const name = m.display_name || '?';
                        return (
                          <div key={i} className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                            width: 24, height: 24, borderRadius: 999, backgroundColor: getMemberAvatarColor(name),
                            border: '2px solid rgba(45,91,255,0.8)', marginLeft: i > 0 ? -7 : 0,
                            zIndex: groupMembers.length - i, position: 'relative',
                            color: '#FFFFFF', fontSize: 9, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
                          }}>
                            {getMemberInitials(name)}
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-white/80 text-sm font-dmsans whitespace-nowrap">
                      {selectedGroup.member_count || groupMembers.length} member{(selectedGroup.member_count || groupMembers.length) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-white/60 text-xs font-dmsans mb-6">{windowCaption}</p>
                </>
              ) : (
                <p className="text-white/80 text-sm font-dmsans mb-6">{windowCaption}</p>
              )}
            </div>
            <GroupSwitcherPill
              selectedGroup={selectedGroup}
              avatarUrl={avatarUrl}
              userName={userName}
              onClick={() => setShowGroupSwitcher(true)}
            />
          </div>

          {/* Months Scroller — with an "All" chip. Hidden for finite group types (All-only). */}
          {!finiteGroup && !isGroupView && (
          <div className={`flex gap-2 overflow-x-auto pb-2 ${isMobile ? '-mx-5 px-5' : ''} no-scrollbar`}>
            <button
              onClick={() => setSelectedMonthKey(ALL_KEY)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold font-dmsans transition-colors ${
                renderEffectiveMonthKey === ALL_KEY
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              All
            </button>
            {monthRange.slice().reverse().map(m => {
              const isSelected = m.key === renderEffectiveMonthKey;
              return (
                <button
                  key={m.key}
                  onClick={() => setSelectedMonthKey(m.key)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold font-dmsans transition-colors ${
                    isSelected
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          )}

          {/* Selected period summary card */}
          {isUnlocked && hasMonthData && (
            <div className="mt-6 bg-white/10 rounded-2xl p-4 border border-white/10 flex items-center justify-between">
              <div className="flex-1 border-r border-white/20 pr-4">
                <p className="text-white/80 text-xs font-dmsans mb-1">
                  {isGroupView ? 'Group spend' : (isAll ? (finiteGroup ? 'Total spent' : 'Spent · last 3 months') : 'Spent this month')}
                </p>
                <p className="text-3xl font-bold font-manrope tracking-tight leading-none mb-1">
                  {formatCurrency(selTotal, activeCurrency)}
                </p>
                <p className="text-white/70 text-xs font-dmsans">{isGroupView ? 'All time' : (isAll ? (finiteGroup ? 'All time' : windowCaption) : selectedMonth?.labelFull)}</p>
              </div>
              <div className="flex-1 pl-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-end gap-2">
                    <span className="text-xl font-bold font-manrope leading-none">{selCount}</span>
                    <span className="text-white/80 text-xs font-dmsans pb-0.5">Receipts</span>
                  </div>
                  {!isAll && vsLastMonthPct !== null && (
                    <div className="inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                      {vsLastMonthDir === 'up' ? <ArrowUpRight className="w-3 h-3 text-white" /> :
                       vsLastMonthDir === 'down' ? <ArrowDownRight className="w-3 h-3 text-white" /> :
                       <Minus className="w-3 h-3 text-white" />}
                      <span className="text-[11px] font-bold font-dmsans text-white">
                        {vsLastMonthPct}% vs last mo
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Currency chips — aligned with content padding */}
      <div className={!isMobile ? 'px-10' : ''}>
        <CurrencyChips
          currencies={allCurrencies}
          selected={activeCurrency}
          onSelect={(cur) => {
            setSelectedCurrency(cur);
            // Keep selectedMonthKey — user stays on the same month
          }}
        />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 relative -mt-4 z-0">
        {!isUnlocked ? renderEmptyState() : !hasMonthData ? renderNoMonthData() : renderInsights()}
      </main>
    </>
  );

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FDFBF7]" data-testid="insights-page">
        {content}
        {/* Bottom Navigation */}
        <nav
          className="fixed bottom-0 left-0 right-0 flex justify-around items-center"
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F3F4F6',
            paddingTop: 10,
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            zIndex: 50
          }}
        >
          {NAV_ITEMS.map((item) => {
            const active = item.key === 'insights';
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="flex flex-col items-center gap-1"
              >
                <item.icon
                  className="w-5 h-5"
                  style={{ color: active ? PRIMARY : '#9CA3AF' }}
                  fill={active ? PRIMARY : 'none'}
                />
                <span
                  style={{
                    color: active ? PRIMARY : '#9CA3AF',
                    fontSize: 11,
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
        <ScanReceiptModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} isMobile={true} onSaved={fetchReceipts} />
        <ReceiptDetailModal
          isOpen={showReceiptDetail}
          onClose={() => setShowReceiptDetail(false)}
          receiptId={selectedReceiptId}
          onDelete={handleReceiptDelete}
          isMobile={true}
        />
        <GroupSwitcherSheet
          isOpen={showGroupSwitcher}
          onClose={() => setShowGroupSwitcher(false)}
          groups={groups}
          selectedGroupId={selectedGroup?.id}
          onSelect={handleGroupSelect}
          onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
          onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
          userName={userName}
          avatarUrl={avatarUrl}
        />

        {/* Outstanding reimbursements modal — mobile */}
        {showOutstandingModal && (
          <>
            <div onClick={() => setShowOutstandingModal(false)} className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
            <div className="fixed left-0 right-0 bottom-0" style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, zIndex: 1001, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="flex justify-center pt-3 pb-2"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
              <div style={{ padding: '0 20px 12px', borderBottom: '1px solid #F3F4F6' }}>
                <p style={{ fontSize: 17, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>Outstanding reimbursements</p>
                <p style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                  {formatCurrency(reimbursementSummary?.pending_total || 0, activeCurrency)} · {outstandingReceipts.length} receipt{outstandingReceipts.length !== 1 ? 's' : ''} pending
                </p>
              </div>
              <div style={{ overflowY: 'auto', padding: '12px 20px 80px' }}>
                {outstandingLoading && <p style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>Loading…</p>}
                {!outstandingLoading && outstandingReceipts.length === 0 && <p style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>All clear!</p>}
                {outstandingReceipts.map(r => (
                  <div key={r.share_id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', marginBottom: 10, backgroundColor: '#FFFBEB' }}>
                    <div className="flex items-center gap-3 p-3">
                      <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                        {(r.merchant || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>{r.merchant || 'Unknown'}</p>
                        <p style={{ fontSize: 11, color: '#B45309', fontFamily: 'DM Sans, sans-serif' }}>
                          {r.paid_by_name || r.shared_by_name} paid
                          {r.paid_by_label_name ? ` · awaiting ${r.paid_by_label_name}` : ''}
                          {r.transaction_date ? ` · ${new Date(r.transaction_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                        </p>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#D97706', fontFamily: 'Manrope, sans-serif', flexShrink: 0 }}>{formatCurrency(r.amount, r.currency || activeCurrency)}</span>
                    </div>
                    {r.is_uploader && expandedMarkId !== r.share_id && (
                      <div style={{ borderTop: '1px solid #FCD34D', padding: '8px 12px' }}>
                        <button onClick={() => setExpandedMarkId(r.share_id)} className="w-full flex items-center justify-center gap-1.5" style={{ backgroundColor: '#D97706', borderRadius: 8, padding: '8px', color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', border: 'none', cursor: 'pointer' }}>
                          ✓ Mark as received
                        </button>
                      </div>
                    )}
                    {r.is_uploader && expandedMarkId === r.share_id && (
                      <div style={{ borderTop: '1px solid #FCD34D', padding: '10px 12px', backgroundColor: '#fff' }}>
                        <input type="text" placeholder="Add a note e.g. received via bank transfer… (optional)" value={markNotes[r.share_id] || ''} onChange={e => setMarkNotes(prev => ({ ...prev, [r.share_id]: e.target.value }))} style={{ width: '100%', border: '1px solid #FCD34D', borderRadius: 7, padding: '6px 10px', fontSize: 16, fontFamily: 'DM Sans, sans-serif', outline: 'none', marginBottom: 6 }} />
                        <div className="flex gap-2">
                          <button onClick={() => handleMarkReceivedInsights(r.share_id, selectedGroup.id)} disabled={markingReceived === r.share_id} style={{ flex: 1, backgroundColor: '#D97706', borderRadius: 7, padding: '7px', color: '#fff', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: markingReceived === r.share_id ? 0.7 : 1 }}>
                            {markingReceived === r.share_id ? 'Saving…' : 'Confirm'}
                          </button>
                          <button onClick={() => setExpandedMarkId(null)} style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 7, padding: '7px', color: '#374151', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {!r.is_uploader && (
                      <div style={{ borderTop: '1px solid #FCD34D', padding: '6px 12px' }}>
                        <p style={{ fontSize: 10, color: '#B45309', textAlign: 'center', fontStyle: 'italic', fontFamily: 'DM Sans, sans-serif' }}>Only {r.shared_by_name?.split(' ')[0] || 'the uploader'} can mark this as received</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="h-screen flex overflow-hidden" style={{ backgroundColor: '#F3F4F6' }} data-testid="insights-page">
      {/* Sidebar */}
      <aside className="flex flex-col" style={{ width: 240, backgroundColor: '#F9FAFB', borderRight: '1px solid #F3F4F6' }}>
        <div className="flex items-center gap-2 px-5 pt-6 pb-8">
          <BuddyLogo size={28} />
          <span style={{ color: '#111827', fontSize: 18, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
            My Buddy
          </span>
        </div>

        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => {
            const active = item.key === 'insights';
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? '#E0E7FF' : 'transparent',
                  color: active ? PRIMARY : '#6B7280',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="flex items-center gap-3">
            <UserAvatar name={userName} avatarUrl={avatarUrl} size={40} fontSize={14} />
            <div className="min-w-0">
              <p className="truncate" style={{ color: '#111827', fontSize: 14, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
                {userName} {lastName}
              </p>
              {userEmail && (
                <p className="truncate" style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                  {userEmail}
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Container */}
      <main className="flex-1 overflow-y-auto bg-[#FDFBF7]">
        {content}
      </main>

      <ScanReceiptModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} isMobile={false} onSaved={fetchReceipts} />
      <ReceiptDetailModal
        isOpen={showReceiptDetail}
        onClose={() => setShowReceiptDetail(false)}
        receiptId={selectedReceiptId}
        onDelete={handleReceiptDelete}
        isMobile={false}
      />
      <GroupSwitcherSheet
        isOpen={showGroupSwitcher}
        onClose={() => setShowGroupSwitcher(false)}
        groups={groups}
        selectedGroupId={selectedGroup?.id}
        onSelect={handleGroupSelect}
        onCreateGroup={() => { setShowGroupSwitcher(false); navigate('/groups/create'); }}
        onManageGroups={() => { setShowGroupSwitcher(false); navigate('/profile'); }}
        userName={userName}
        avatarUrl={avatarUrl}
      />

      {/* Outstanding reimbursements modal */}
      {showOutstandingModal && (
        <>
          <div onClick={() => setShowOutstandingModal(false)} className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
          <div className="fixed left-0 right-0 bottom-0" style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, zIndex: 1001, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="flex justify-center pt-3 pb-2"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: '#D1D5DB' }} /></div>
            <div style={{ padding: '0 20px 12px', borderBottom: '1px solid #F3F4F6' }}>
              <p style={{ fontSize: 17, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>Outstanding reimbursements</p>
              <p style={{ fontSize: 12, color: '#9CA3AF', fontFamily: 'DM Sans, sans-serif', marginTop: 2 }}>
                {formatCurrency(reimbursementSummary?.pending_total || 0, activeCurrency)} · {outstandingReceipts.length} receipt{outstandingReceipts.length !== 1 ? 's' : ''} pending
              </p>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 20px 80px' }}>
              {outstandingLoading && <p style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>Loading…</p>}
              {!outstandingLoading && outstandingReceipts.length === 0 && <p style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>All clear!</p>}
              {outstandingReceipts.map(r => (
                <div key={r.share_id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', marginBottom: 10, backgroundColor: '#FFFBEB' }}>
                  <div className="flex items-center gap-3 p-3">
                    <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                      {(r.merchant || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>{r.merchant || 'Unknown'}</p>
                      <p style={{ fontSize: 11, color: '#B45309', fontFamily: 'DM Sans, sans-serif' }}>
                        {r.paid_by_name || r.shared_by_name} paid
                        {r.paid_by_label_name ? ` · awaiting ${r.paid_by_label_name}` : ''}
                        {r.transaction_date ? ` · ${new Date(r.transaction_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                      </p>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#D97706', fontFamily: 'Manrope, sans-serif', flexShrink: 0 }}>{formatCurrency(r.amount, r.currency || activeCurrency)}</span>
                  </div>
                  {/* Mark received — uploader only */}
                  {r.is_uploader && expandedMarkId !== r.share_id && (
                    <div style={{ borderTop: '1px solid #FCD34D', padding: '8px 12px' }}>
                      <button
                        onClick={() => setExpandedMarkId(r.share_id)}
                        className="w-full flex items-center justify-center gap-1.5"
                        style={{ backgroundColor: '#D97706', borderRadius: 8, padding: '8px', color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', border: 'none', cursor: 'pointer' }}
                      >
                        ✓ Mark as received
                      </button>
                    </div>
                  )}
                  {r.is_uploader && expandedMarkId === r.share_id && (
                    <div style={{ borderTop: '1px solid #FCD34D', padding: '10px 12px', backgroundColor: '#fff' }}>
                      <input
                        type="text"
                        placeholder="Add a note e.g. received via bank transfer… (optional)"
                        value={markNotes[r.share_id] || ''}
                        onChange={e => setMarkNotes(prev => ({ ...prev, [r.share_id]: e.target.value }))}
                        style={{ width: '100%', border: '1px solid #FCD34D', borderRadius: 7, padding: '6px 10px', fontSize: 12, fontFamily: 'DM Sans, sans-serif', outline: 'none', marginBottom: 6 }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleMarkReceivedInsights(r.share_id, selectedGroup.id)}
                          disabled={markingReceived === r.share_id}
                          style={{ flex: 1, backgroundColor: '#D97706', borderRadius: 7, padding: '7px', color: '#fff', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: markingReceived === r.share_id ? 0.7 : 1 }}
                        >
                          {markingReceived === r.share_id ? 'Saving…' : 'Confirm'}
                        </button>
                        <button onClick={() => setExpandedMarkId(null)} style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 7, padding: '7px', color: '#374151', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {!r.is_uploader && (
                    <div style={{ borderTop: '1px solid #FCD34D', padding: '6px 12px' }}>
                      <p style={{ fontSize: 10, color: '#B45309', textAlign: 'center', fontStyle: 'italic', fontFamily: 'DM Sans, sans-serif' }}>Only {r.shared_by_name?.split(' ')[0] || 'the uploader'} can mark this as received</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}